// src/hooks/useFlowSenseSnapshot.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — one live snapshot of the whole system for the dashboard AI
//
// Loads (in parallel, each source can fail on its own): work orders, active sensor
// alerts, issues, ML assets-at-risk, low stock, SLA metrics and AI engine status,
// plus service health (useSystemHealth) and the WebSocket state. Rebuilds the brief
// (flowsenseIntel.buildSystemBrief) and refreshes silently on live events
// (coalesced) and every 2 minutes while the tab is visible.
//
// Work orders / alerts / issues go through services/dashboardDataService, so the
// widgets and the AI share one request; live refreshes bypass the 5-minute cache.
// ─────────────────────────────────────────────────────────────────────────────
// --- UPDATE (road AI / ML): new source `roads` = GET /roads/ai-summary (segment ML coverage
// and PCI, worst roads, road jobs, paving QA, inspections, road RUL) → snapshot.roads, used by
// flowsenseIntel for road priorities, the Road Works module status and road answers.
// The endpoint is cached 15 s server-side, so live refreshes stay cheap.
// --- END UPDATE
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchAllWorkOrdersShared, fetchActiveAlertsShared, fetchAllIssuesShared } from '../services/dashboardDataService';
import { fetchAssetsAtRisk } from '../services/predictiveAlertService';
import inventoryService from '../services/inventoryService';
import { authenticatedFetch } from '../services/api';
import { getFlowSenseAiStatus } from '../services/flowsenseAiService';
import useSystemHealth from './useSystemHealth';
import { useNotifications } from '../contexts/NotificationContext';
import useFlowSenseLive from './useFlowSenseLive';
import { buildSystemBrief } from '../components/dashboard/ai/flowsenseIntel';

export const SNAPSHOT_POLL_MS = 120000;
export const SNAPSHOT_LIVE_DEBOUNCE_MS = 4000;
const LIVE_GROUPS_ALL = ['workOrders', 'sla', 'alerts', 'predictions', 'issues', 'assets', 'roads', 'inventory'];

const settle = async (key, fn, pick, live) => {
    try {
        const res = await fn(live);
        return [key, 'ok', pick(res)];
    } catch (e) {
        return [key, 'error', null];
    }
};

const SOURCES = [
    // live refreshes bypass the work order service cache (shared + de-duplicated across widgets)
    ['workOrders', (live) => fetchAllWorkOrdersShared({ live }), r => r?.data || r?.workOrders || []],
    ['alerts', (live) => fetchActiveAlertsShared({ live }), r => r?.data || (Array.isArray(r) ? r : [])],
    ['issues', (live) => fetchAllIssuesShared({ live }), r => r?.data || r?.issues || []],
    ['risk', () => fetchAssetsAtRisk(), r => r?.data || (Array.isArray(r) ? r : [])],
    ['stock', () => inventoryService.getLowStockAlerts(), r => r?.alerts || r?.data || []],
    ['sla', () => authenticatedFetch('/analytics/sla-metrics'), (r) => {
        const d = r?.data || r || {};
        return {
            complianceRate: parseFloat(d.compliance_rate ?? d.complianceRate ?? 0),
            atRisk: Number(d.at_risk ?? d.currentlyAtRisk ?? 0),
            breached: Number(d.breached ?? d.currentlyBreached ?? 0),
        };
    }],
    ['ai', () => getFlowSenseAiStatus(), r => r || null],
    // --- UPDATE: road network AI / ML roll-up
    ['roads', () => authenticatedFetch('/roads/ai-summary'), r => r?.data || null],
    // --- END UPDATE
];

export default function useFlowSenseSnapshot({ enabled = true } = {}) {
    const [data, setData] = useState(null);
    const [sources, setSources] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const mountedRef = useRef(true);
    const reqRef = useRef(0);

    const { health } = useSystemHealth();
    const notifications = useNotifications();
    const wsConnected = notifications ? notifications.isConnected : undefined;

    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

    const load = useCallback(async (silent = false) => {
        const rid = ++reqRef.current;
        if (silent) setIsRefreshing(true); else setIsLoading(true);
        const results = await Promise.all(SOURCES.map(([k, fn, pick]) => settle(k, fn, pick, silent)));
        if (!mountedRef.current || rid !== reqRef.current) return;
        const next = {};
        const st = {};
        results.forEach(([k, status, value]) => { st[k] = status; next[k] = value; });
        setData(next);
        setSources(st);
        setLastUpdated(new Date());
        setIsLoading(false);
        setIsRefreshing(false);
    }, []);

    useEffect(() => { if (enabled) load(false); }, [enabled, load]);

    // Live: any change in the system → one silent refresh per burst
    useFlowSenseLive(LIVE_GROUPS_ALL, () => load(true), { debounceMs: SNAPSHOT_LIVE_DEBOUNCE_MS, enabled });

    // Fallback polling while visible
    useEffect(() => {
        if (!enabled) return undefined;
        const id = setInterval(() => { if (document.visibilityState !== 'hidden') load(true); }, SNAPSHOT_POLL_MS);
        return () => clearInterval(id);
    }, [enabled, load]);

    const snapshot = useMemo(() => (data ? {
        workOrders: data.workOrders || [],
        alerts: data.alerts || [],
        issues: data.issues || [],
        risk: data.risk || [],
        lowStock: data.stock || [],
        sla: data.sla,
        aiStatus: data.ai,
        roads: data.roads || null, // --- UPDATE
        health: health || {},
        wsConnected,
        sources,
        at: lastUpdated ? lastUpdated.toISOString() : null,
    } : null), [data, health, wsConnected, sources, lastUpdated]);

    const brief = useMemo(() => (snapshot ? buildSystemBrief(snapshot) : null), [snapshot]);

    const refresh = useCallback(() => load(true), [load]);
    return useMemo(
        () => ({ snapshot, brief, isLoading, isRefreshing, lastUpdated, refresh }),
        [snapshot, brief, isLoading, isRefreshing, lastUpdated, refresh]
    );
}
